export const sfx = (src, vol = 0.4) => {
  try {
    const a = new Audio(src);
    a.volume = vol;
    a.play().catch(() => {});
  } catch {}
};

let radarCtx = null;

export const unlockRadarAudio = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    radarCtx = radarCtx || new Ctx();
    if (radarCtx.state === "suspended") radarCtx.resume();
  } catch {}
};

// soft sonar ping as the sweep crosses a titan blip

export const pingSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    const osc = radarCtx.createOscillator();
    const gain = radarCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = 940;
    gain.gain.setValueAtTime(0.05, radarCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, radarCtx.currentTime + 0.45);
    osc.connect(gain).connect(radarCtx.destination);
    osc.start();
    osc.stop(radarCtx.currentTime + 0.5);
  } catch {}
};

// quick two-tone chirp when a gold signal spike is caught

export const catchSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    const osc = radarCtx.createOscillator();
    const gain = radarCtx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1320, radarCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1760, radarCtx.currentTime + 0.14);
    gain.gain.setValueAtTime(0.08, radarCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, radarCtx.currentTime + 0.2);
    osc.connect(gain).connect(radarCtx.destination);
    osc.start();
    osc.stop(radarCtx.currentTime + 0.22);
  } catch {}
};

// descending buzz when a worm contact is scared off

export const scareSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    const osc = radarCtx.createOscillator();
    const gain = radarCtx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(220, radarCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(88, radarCtx.currentTime + 0.22);
    gain.gain.setValueAtTime(0.07, radarCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, radarCtx.currentTime + 0.26);
    osc.connect(gain).connect(radarCtx.destination);
    osc.start();
    osc.stop(radarCtx.currentTime + 0.28);
  } catch {}
};

// low square throb when a worm jams a runner's mission clock

export const jamSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    const osc = radarCtx.createOscillator();
    const gain = radarCtx.createGain();
    osc.type = "square";
    osc.frequency.value = 70;
    gain.gain.setValueAtTime(0.09, radarCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, radarCtx.currentTime + 0.4);
    osc.connect(gain).connect(radarCtx.destination);
    osc.start();
    osc.stop(radarCtx.currentTime + 0.42);
  } catch {}
};

// radar grid skins (Shard Shop cosmetics) — rgb triplet feeds the G() alpha helper

export const feverSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    [523, 659, 784, 1047].forEach((f, i) => {
      const osc = radarCtx.createOscillator();
      const gain = radarCtx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      const t0 = radarCtx.currentTime + i * 0.07;
      gain.gain.setValueAtTime(0.09, t0);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
      osc.connect(gain).connect(radarCtx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.32);
    });
  } catch {}
};

// critter chirp — tiny two-note whistle

export const critterSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    const osc = radarCtx.createOscillator();
    const gain = radarCtx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1568, radarCtx.currentTime);
    osc.frequency.setValueAtTime(2093, radarCtx.currentTime + 0.09);
    gain.gain.setValueAtTime(0.08, radarCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, radarCtx.currentTime + 0.22);
    osc.connect(gain).connect(radarCtx.destination);
    osc.start();
    osc.stop(radarCtx.currentTime + 0.24);
  } catch {}
};

// every critter species sings its own tiny call — pitch, chirp count, waveform
// and warble are derived from its id, so kids can learn all 50 by ear

export const critterCry = (id) => {
  try {
    unlockRadarAudio();
    if (!radarCtx) return;
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const base = 420 + (h % 640); // species pitch 420-1060 Hz
    const notes = 2 + ((h >> 4) % 3); // 2-4 chirps
    const gap = 0.08 + ((h >> 7) % 3) * 0.035; // chirp spacing
    const wave = ["sine", "triangle", "square"][(h >> 2) % 3];
    const bend = ((h >> 9) % 2 ? 1 : -1) * (0.12 + ((h >> 10) % 4) * 0.05); // rising/falling warble
    const sing = () => {
      for (let n = 0; n < notes; n++) {
        const t = radarCtx.currentTime + n * gap;
        const osc = radarCtx.createOscillator();
        const gain = radarCtx.createGain();
        osc.type = wave;
        osc.frequency.setValueAtTime(base * (1 + bend * (n % 2)), t);
        osc.frequency.exponentialRampToValueAtTime(base * (1 + bend * ((n + 1) % 2)), t + gap * 0.75);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.12, t + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + gap * 0.85);
        osc.connect(gain).connect(radarCtx.destination);
        osc.start(t);
        osc.stop(t + gap);
      }
    };
    if (radarCtx.state === "running") sing();
    else radarCtx.resume().then(sing).catch(() => {});
  } catch {}
};

// soft warm blip when a titan waves back

export const waveSfx = () => {
  try {
    if (!radarCtx || radarCtx.state !== "running") return;
    const osc = radarCtx.createOscillator();
    const gain = radarCtx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(660, radarCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(880, radarCtx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.07, radarCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, radarCtx.currentTime + 0.18);
    osc.connect(gain).connect(radarCtx.destination);
    osc.start();
    osc.stop(radarCtx.currentTime + 0.2);
  } catch {}
};
